import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, Calendar, FileText, HardDrive, RefreshCw, AlertTriangle, Wifi, WifiOff, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import LazyImage from './LazyImage';
import { preloadImages, loadImageWithCache } from '../utils/imageCache';
import { getAbsoluteImageUrl, resolveImageUrl, addCacheBuster } from '../utils/urlUtils';
import { retryImageOperation, monitorConnectivity } from '../utils/networkResilience';
import { parseBackendError, getErrorMessage, isRetryableError, ERROR_TYPES } from '../utils/errorHandling';

const ImageModal = ({
  images = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
  onNavigate
}) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(null);
  const [retryAttempts, setRetryAttempts] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [currentImageUrls, setCurrentImageUrls] = useState({});
  const [fallbackIndex, setFallbackIndex] = useState({});

  // Monitor network status with enhanced connectivity monitoring
  useEffect(() => {
    const cleanup = monitorConnectivity((online) => {
      setIsOnline(online);
      if (online && imageError) {
        // Auto-retry when coming back online if there was an error
        const currentImage = images[activeIndex];
        if (currentImage && isRetryableError(imageError)) {
          console.log('Network restored, auto-retrying failed image');
          // Use setTimeout to avoid calling handleRetryImage during render
          setTimeout(() => {
            handleRetryImage();
          }, 100);
        }
      }
    });

    return cleanup;
  }, [imageError, images, activeIndex]);

  // Update active index when currentIndex prop changes
  useEffect(() => {
    setActiveIndex(currentIndex);
  }, [currentIndex]);

  // Reset states when modal opens/closes or active index changes
  useEffect(() => {
    if (isOpen) {
      setImageLoading(true);
      setImageError(null);
      setDownloadProgress(null);
      
      // Generate URLs with fallbacks for current image
      const currentImage = images[activeIndex];
      if (currentImage) {
        const urlData = resolveImageUrl(currentImage.url || currentImage.preview, {
          generateFallbacks: true,
          addCacheBuster: false
        });
        setCurrentImageUrls(prev => ({
          ...prev,
          [currentImage.id]: urlData
        }));
        setFallbackIndex(prev => ({
          ...prev,
          [currentImage.id]: 0
        }));
      }
      
      // Preload adjacent images for smoother navigation
      preloadAdjacentImages();
    }
  }, [isOpen, activeIndex, images]);

  // Preload adjacent images for smoother navigation
  const preloadAdjacentImages = async () => {
    if (!images || images.length <= 1) return;

    const imagesToPreload = [];
    
    // Add previous image
    const prevIndex = activeIndex > 0 ? activeIndex - 1 : images.length - 1;
    if (images[prevIndex]?.url) {
      imagesToPreload.push(getAbsoluteImageUrl(images[prevIndex].url));
    }
    
    // Add next image
    const nextIndex = activeIndex < images.length - 1 ? activeIndex + 1 : 0;
    if (images[nextIndex]?.url) {
      imagesToPreload.push(getAbsoluteImageUrl(images[nextIndex].url));
    }

    if (imagesToPreload.length > 0) {
      try {
        await preloadImages(imagesToPreload, { batchSize: 2 });
      } catch (error) {
        console.error('Error preloading adjacent images:', error);
      }
    }
  };

  // Navigation functions
  const navigatePrevious = useCallback(() => {
    if (images.length <= 1) return;
    
    const newIndex = activeIndex > 0 ? activeIndex - 1 : images.length - 1;
    setActiveIndex(newIndex);
    
    if (onNavigate) {
      onNavigate(newIndex);
    }
  }, [activeIndex, images.length, onNavigate]);

  const navigateNext = useCallback(() => {
    if (images.length <= 1) return;
    
    const newIndex = activeIndex < images.length - 1 ? activeIndex + 1 : 0;
    setActiveIndex(newIndex);
    
    if (onNavigate) {
      onNavigate(newIndex);
    }
  }, [activeIndex, images.length, onNavigate]);

  // Enhanced image retry with exponential backoff and better error handling
  const handleRetryImage = useCallback(async () => {
    const currentImage = images[activeIndex];
    if (!currentImage) return;

    const currentAttempts = retryAttempts[currentImage.id] || 0;
    if (currentAttempts >= 3) {
      const errorMsg = getErrorMessage(ERROR_TYPES.GENERIC_ERROR, {
        filename: currentImage.filename,
        retryCount: currentAttempts
      });
      toast.error(errorMsg.message);
      return;
    }

    setRetryAttempts(prev => ({ ...prev, [currentImage.id]: currentAttempts + 1 }));
    setImageLoading(true);
    setImageError(null);

    try {
      // Use the network resilience service for retry with exponential backoff
      await retryImageOperation(async () => {
        return new Promise((resolve, reject) => {
          const imgElement = document.querySelector('.modal-main-image');
          if (!imgElement) {
            reject(new Error('Image element not found'));
            return;
          }

          // Try with cache buster first
          const urlData = currentImageUrls[currentImage.id];
          let retryUrl = urlData?.primary || currentImage.url || currentImage.preview;
          retryUrl = addCacheBuster(retryUrl, true);

          const handleLoad = () => {
            imgElement.removeEventListener('load', handleLoad);
            imgElement.removeEventListener('error', handleError);
            resolve();
          };

          const handleError = (event) => {
            imgElement.removeEventListener('load', handleLoad);
            imgElement.removeEventListener('error', handleError);
            reject(new Error('Image failed to load'));
          };

          imgElement.addEventListener('load', handleLoad);
          imgElement.addEventListener('error', handleError);
          imgElement.src = retryUrl;
        });
      }, {
        maxRetries: 1, // We handle retries at component level
        operationName: 'image_modal_retry',
        filename: currentImage.filename
      });

      // Success
      handleImageLoad();
      toast.success(`Image loaded successfully (attempt ${currentAttempts + 1})`);
    } catch (error) {
      const parsedError = parseBackendError(error, {
        operation: 'image_retry',
        filename: currentImage.filename,
        retryCount: currentAttempts + 1
      });
      
      setImageError(parsedError);
      const errorMsg = getErrorMessage(parsedError.type, parsedError.context);
      toast.error(errorMsg.message);
    }
  }, [images, activeIndex, retryAttempts, currentImageUrls]);

  // Enhanced download with progress tracking and comprehensive error handling
  const handleDownload = useCallback(async () => {
    const currentImage = images[activeIndex];
    if (!currentImage) return;

    if (!isOnline) {
      const errorMsg = getErrorMessage(ERROR_TYPES.OFFLINE, {
        filename: currentImage.filename
      });
      toast.error(errorMsg.message);
      return;
    }

    try {
      setDownloadProgress(0);
      const imageUrl = currentImage.url || currentImage.preview;
      if (!imageUrl) {
        const errorMsg = getErrorMessage(ERROR_TYPES.IMAGE_NOT_FOUND, {
          filename: currentImage.filename
        });
        toast.error(errorMsg.message);
        return;
      }

      // For blob URLs (preview images), handle differently
      if (imageUrl.startsWith('blob:')) {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = currentImage.filename || currentImage.original_filename || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Image downloaded successfully');
        setDownloadProgress(null);
        return;
      }

      // Use network resilience for download with retry logic
      await retryImageOperation(async () => {
        const absoluteUrl = getAbsoluteImageUrl(imageUrl);
        
        // Try multiple URLs if available
        const urlData = currentImageUrls[currentImage.id];
        const urlsToTry = [absoluteUrl];
        if (urlData?.fallbacks) {
          urlsToTry.push(...urlData.fallbacks);
        }

        let lastError = null;
        
        for (const tryUrl of urlsToTry) {
          try {
            const response = await fetch(tryUrl);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            // Get content info
            const contentLength = response.headers.get('content-length');
            const contentType = response.headers.get('content-type');
            const total = parseInt(contentLength, 10);
            let loaded = 0;

            // Read response with progress tracking
            const reader = response.body.getReader();
            const chunks = [];

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              chunks.push(value);
              loaded += value.length;

              if (total && total > 0) {
                const progress = Math.round((loaded / total) * 100);
                setDownloadProgress(progress);
              } else {
                // Indeterminate progress
                setDownloadProgress(Math.min(50 + (loaded / 1024 / 1024) * 10, 90));
              }
            }

            // Create blob and download
            const blob = new Blob(chunks, { type: contentType || 'image/*' });
            const downloadUrl = window.URL.createObjectURL(blob);
            
            // Determine filename with proper extension
            let filename = currentImage.filename || currentImage.original_filename || 'image';
            if (!filename.includes('.') && contentType) {
              const extension = contentType.split('/')[1];
              if (extension && ['jpeg', 'jpg', 'png', 'webp', 'gif'].includes(extension)) {
                filename += `.${extension === 'jpeg' ? 'jpg' : extension}`;
              }
            }
            
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(link);
            
            setDownloadProgress(100);
            toast.success(`Image downloaded successfully: ${filename}`);
            return; // Success, exit the loop
            
          } catch (error) {
            lastError = error;
            console.warn(`Download failed for URL ${tryUrl}:`, error.message);
            continue; // Try next URL
          }
        }
        
        // If we get here, all URLs failed
        throw lastError || new Error('All download URLs failed');
        
      }, {
        maxRetries: 2,
        operationName: 'image_download',
        filename: currentImage.filename
      });

    } catch (error) {
      console.error('Error downloading image:', error);
      
      // Parse error using our error handling system
      const parsedError = parseBackendError(error, {
        operation: 'image_download',
        filename: currentImage.filename
      });
      
      const errorMsg = getErrorMessage(parsedError.type, parsedError.context);
      toast.error(errorMsg.message);
      
    } finally {
      // Clear progress after a short delay to show completion
      setTimeout(() => {
        setDownloadProgress(null);
      }, 1000);
    }
  }, [images, activeIndex, isOnline, currentImageUrls]);

  // Enhanced keyboard navigation with better accessibility
  const handleKeyDown = useCallback((event) => {
    if (!isOpen) return;

    // Prevent default for all handled keys
    const handledKeys = ['Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'r', 'R', 'd', 'D'];
    if (handledKeys.includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        navigatePrevious();
        break;
      case 'ArrowRight':
        navigateNext();
        break;
      case 'Home':
        // Go to first image
        if (images.length > 1) {
          setActiveIndex(0);
          if (onNavigate) onNavigate(0);
        }
        break;
      case 'End':
        // Go to last image
        if (images.length > 1) {
          const lastIndex = images.length - 1;
          setActiveIndex(lastIndex);
          if (onNavigate) onNavigate(lastIndex);
        }
        break;
      case 'r':
      case 'R':
        // Retry current image
        if (imageError) {
          handleRetryImage();
        }
        break;
      case 'd':
      case 'D':
        // Download current image
        if (!downloadProgress) {
          handleDownload();
        }
        break;
      default:
        break;
    }
  }, [isOpen, onClose, images.length, imageError, downloadProgress, navigatePrevious, navigateNext, onNavigate, handleRetryImage, handleDownload]);

  // Add keyboard event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Enhanced image load and error handling
  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(null);
    // Reset retry attempts on successful load
    const currentImage = images[activeIndex];
    if (currentImage && retryAttempts[currentImage.id]) {
      setRetryAttempts(prev => ({ ...prev, [currentImage.id]: 0 }));
    }
  };

  const handleImageError = async (error) => {
    setImageLoading(false);
    
    const currentImage = images[activeIndex];
    if (!currentImage) return;

    // Parse the error using our error handling system
    const parsedError = parseBackendError(error, {
      operation: 'image_display',
      filename: currentImage.filename || currentImage.original_filename,
      imageId: currentImage.id
    });

    // Try fallback URLs before showing error
    const urlData = currentImageUrls[currentImage.id];
    const currentFallbackIndex = fallbackIndex[currentImage.id] || 0;
    
    if (urlData && urlData.fallbacks && currentFallbackIndex < urlData.fallbacks.length) {
      console.log(`Trying fallback URL ${currentFallbackIndex + 1}/${urlData.fallbacks.length} for image:`, currentImage.filename);
      
      // Update fallback index
      setFallbackIndex(prev => ({
        ...prev,
        [currentImage.id]: currentFallbackIndex + 1
      }));
      
      // Try the next fallback URL
      const fallbackUrl = urlData.fallbacks[currentFallbackIndex];
      const imgElement = document.querySelector('.modal-main-image');
      if (imgElement) {
        imgElement.src = fallbackUrl;
        return; // Don't set error state yet, let the fallback attempt complete
      }
    }

    // All fallbacks exhausted or no fallbacks available
    setImageError(parsedError);
    console.error('Failed to load modal image after all attempts:', parsedError);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };



  // Don't render if not open or no images
  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const currentImage = images[activeIndex];
  if (!currentImage) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50">
      {/* Modal Container */}
      <div className="relative w-full h-full max-w-6xl max-h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-black bg-opacity-50 text-white">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold truncate">
              {currentImage.filename || currentImage.original_filename || 'Image'}
            </h2>
            {images.length > 1 && (
              <span className="text-sm text-gray-300">
                {activeIndex + 1} of {images.length}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors relative"
              title="Download image"
              disabled={downloadProgress !== null}
            >
              {downloadProgress !== null ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span className="text-xs">{downloadProgress}%</span>
                </div>
              ) : (
                <Download size={20} />
              )}
            </button>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Close (Esc)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex items-center justify-center relative">
          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              {/* Previous Button */}
              <button
                onClick={navigatePrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full transition-colors"
                title="Previous image (←)"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Next Button */}
              <button
                onClick={navigateNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full transition-colors"
                title="Next image (→)"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Image Container */}
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Loading State */}
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            )}

            {/* Error State */}
            {imageError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8">
                <div className="flex items-center mb-4">
                  <AlertTriangle size={48} className="text-red-500 mr-3" />
                  {!isOnline ? (
                    <WifiOff size={24} className="text-red-500" />
                  ) : (
                    <Wifi size={24} className="text-gray-400" />
                  )}
                </div>
                <p className="text-lg font-medium mb-2">Failed to load image</p>
                <p className="text-sm text-gray-400 mb-4 text-center max-w-md">
                  {!isOnline 
                    ? 'No internet connection. Please check your network and try again.'
                    : 'The image could not be displayed. This may be due to a network issue or the image file being unavailable.'
                  }
                </p>
                
                {/* Retry attempts info */}
                {(retryAttempts[currentImage.id] || 0) > 0 && (
                  <p className="text-xs text-gray-500 mb-4">
                    Retry attempts: {retryAttempts[currentImage.id]}/3
                  </p>
                )}
                
                {/* Retry button */}
                {(retryAttempts[currentImage.id] || 0) < 3 && (
                  <button
                    onClick={handleRetryImage}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    title="Retry loading image"
                  >
                    <RefreshCw size={16} />
                    <span>Retry</span>
                  </button>
                )}
                
                {/* Max retries reached */}
                {(retryAttempts[currentImage.id] || 0) >= 3 && (
                  <div className="text-center">
                    <p className="text-sm text-red-400 mb-2">Maximum retry attempts reached</p>
                    <p className="text-xs text-gray-500">Please check your connection or contact support</p>
                  </div>
                )}
              </div>
            )}

            {/* Lazy Image */}
            {!imageError && (
              <LazyImage
                src={getAbsoluteImageUrl(currentImage.url || currentImage.preview)}
                thumbnailSrc={currentImage.thumbnail?.dataUrl || currentImage.thumbnail_url}
                alt={currentImage.filename || currentImage.original_filename || 'Asset image'}
                className="max-w-full max-h-full object-contain modal-main-image"
                onLoad={(event) => {
                  handleImageLoad();
                  if (event.cached) {
                    console.log(`Modal image loaded from cache: ${currentImage.filename}`);
                  }
                }}
                onError={(event) => {
                  handleImageError();
                  console.error(`Failed to load modal image: ${currentImage.filename}`, event.error);
                }}
                style={{ display: imageLoading ? 'none' : 'block' }}
                cacheOptions={{ quality: 0.9 }} // High quality for modal view
                retryable={true}
              />
            )}
          </div>
        </div>

        {/* Footer with Image Metadata */}
        <div className="p-4 bg-black bg-opacity-50 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {/* File Info */}
            <div className="flex items-center space-x-2">
              <FileText size={16} className="text-gray-400" />
              <div>
                <p className="font-medium">Filename</p>
                <p className="text-gray-300 truncate">
                  {currentImage.filename || currentImage.original_filename || 'Unknown'}
                </p>
              </div>
            </div>

            {/* File Size */}
            <div className="flex items-center space-x-2">
              <HardDrive size={16} className="text-gray-400" />
              <div>
                <p className="font-medium">File Size</p>
                <p className="text-gray-300">
                  {formatFileSize(currentImage.size)}
                </p>
              </div>
            </div>

            {/* Upload Date */}
            <div className="flex items-center space-x-2">
              <Calendar size={16} className="text-gray-400" />
              <div>
                <p className="font-medium">Upload Date</p>
                <p className="text-gray-300">
                  {formatDate(currentImage.uploaded_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Additional metadata if available */}
          {(currentImage.content_type || currentImage.uploaded_by) && (
            <div className="mt-4 pt-4 border-t border-gray-600">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {currentImage.content_type && (
                  <div>
                    <p className="font-medium text-gray-400">Content Type</p>
                    <p className="text-gray-300">{currentImage.content_type}</p>
                  </div>
                )}
                {currentImage.uploaded_by && (
                  <div>
                    <p className="font-medium text-gray-400">Uploaded By</p>
                    <p className="text-gray-300">{currentImage.uploaded_by}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Thumbnail Navigation for Multiple Images */}
        {images.length > 1 && (
          <div className="p-4 bg-black bg-opacity-50">
            <div className="flex space-x-2 overflow-x-auto">
              {images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => {
                    setActiveIndex(index);
                    if (onNavigate) {
                      onNavigate(index);
                    }
                  }}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                    index === activeIndex
                      ? 'border-cyan-500'
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <LazyImage
                    src={getAbsoluteImageUrl(image.thumbnail_url || image.url || image.preview)}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    cacheOptions={{ width: 64, height: 64 }} // Small thumbnail cache
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close */}
      <div
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export default ImageModal;